import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Rocket, Shield, Code, Network, Zap, Globe, Lock, ExternalLink } from 'lucide-react';

const Roadmap = () => {
  const phases = [
    {
      phase: 'Phase 1',
      title: 'Proof of Concept',
      status: 'completed',
      icon: CheckCircle2,
      date: 'Q4 2024 - Q1 2025',
      achievements: [
        'DAG (Directed Acyclic Graph) implementation with GHOSTDAG',
        '4-shard system with cross-shard atomic transactions',
        'REVM (Rust EVM) integration for Ethereum compatibility',
        'FlowConsensus hybrid consensus with BLS aggregation',
        'Local testnet running on localhost:9545',
        'Fully functional CLI (wallet, staking, governance)',
        'Block Explorer with shard and transaction visualization'
      ]
    },
    {
      phase: 'Phase 2',
      title: 'Public Testnet',
      status: 'in-progress',
      icon: Loader2,
      date: 'Q2 2025 - Q3 2025',
      achievements: [
        'Threshold encryption for MEV resistance implemented',
        'Data Availability Layer with Reed-Solomon erasure coding',
        'Checkpointing system to Ethereum L1',
        'Performance optimization (target: 520,000+ TPS)',
        'Public testnet with faucet and complete documentation',
        'Real-time performance metrics',
        'On-chain governance with 7 multisig treasuries'
      ]
    },
    {
      phase: 'Phase 3',
      title: 'Audits and Security',
      status: 'planned',
      icon: Shield,
      date: 'Q4 2025 - Q1 2026',
      achievements: [
        'Formal audit of contracts and core protocol',
        'Complete zk-Proofs implementation for privacy',
        'Bug bounty program with AVO rewards',
        'Fuzzing and exhaustive edge case testing',
        'Complete technical documentation and final whitepaper',
        'Large-scale network simulations',
        'Security certifications'
      ]
    },
    {
      phase: 'Phase 4',
      title: 'Mainnet Launch',
      status: 'planned',
      icon: Rocket,
      date: 'Q2 2026',
      achievements: [
        'Gradual mainnet launch with capacity limits',
        'Bridges to Ethereum, BSC, and other major networks',
        'Wallet integrations (MetaMask, WalletConnect, etc.)',
        'First DeFi protocols deployed (DEX, Lending)',
        'Early adopter incentive program',
        'Shard expansion based on demand',
        'Public staking and active delegation'
      ]
    }
  ];

  const futureFeatures = [
    {
      icon: Code,
      title: 'Complete SDK',
      description: 'TypeScript/JavaScript SDK to facilitate dApp development with examples and templates.'
    },
    {
      icon: Network,
      title: 'Dynamic Sharding',
      description: 'Ability to add/remove shards dynamically based on network demand.'
    },
    {
      icon: Zap,
      title: 'Layer 2 Support',
      description: 'Support for L2 solutions built on top of AVO for ultra-scalability.'
    },
    {
      icon: Globe,
      title: 'Multi-Chain Bridges',
      description: 'Native bridges to multiple blockchains for total interoperability.'
    },
    {
      icon: Lock,
      title: 'Enhanced Privacy',
      description: 'Optional private transactions implementation with zk-SNARKs.'
    },
    {
      icon: Rocket,
      title: 'Optimizations',
      description: 'Continuous performance improvements and latency reduction.'
    }
  ];

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed': 
        return <span className="px-3 py-1 bg-white text-black text-xs font-bold">COMPLETED</span>;
      case 'in-progress': 
        return <span className="px-3 py-1 bg-white text-black text-xs font-bold">IN PROGRESS</span>;
      case 'planned': 
        return <span className="px-3 py-1 bg-gray-700 text-gray-300 text-xs font-bold">PLANNED</span>;
      default: 
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-20">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="container mx-auto px-6 mb-20"
      >
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            Roadmap
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our journey towards an ultra-high performance DeFi blockchain with hybrid consensus and dynamic sharding.
          </p>
        </div>
      </motion.section>

      {/* Timeline */}
      <section className="container mx-auto px-6 mb-20">
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {phases.map((phase, index) => {
            const Icon = phase.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-black border-2 border-white p-8 hover:border-blue-400 transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">{phase.phase}</p>
                    <h3 className="text-2xl font-bold text-white mb-2">{phase.title}</h3>
                    <p className="text-sm text-gray-400">{phase.date}</p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <Icon className={`w-10 h-10 text-white ${phase.status === 'in-progress' ? 'animate-spin' : ''}`} />
                    {getStatusBadge(phase.status)}
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-white mb-6"></div>

                {/* Achievements */}
                <div className="space-y-3">
                  {phase.achievements.map((achievement, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-white mt-1 text-sm">◉</span>
                      <p className="text-sm text-gray-300 leading-relaxed">{achievement}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Future Features */}
      <section className="container mx-auto px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-4xl font-bold text-center mb-4 text-white">Beyond Mainnet</h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            After mainnet launch, we will continue innovating and improving the protocol 
            with new features and optimizations.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {futureFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-black border-2 border-white p-6 hover:border-purple-400 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-black border-2 border-white group-hover:border-purple-400 transition-all">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2 text-white">{feature.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="container mx-auto px-6"
      >
        <div className="bg-black border-2 border-white p-12 text-center max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 text-white">Join the Journey</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            AVO Protocol is in active development. Follow our progress, test the testnet, 
            and be part of building the future of DeFi.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="http://localhost:3000"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-white text-black font-bold hover:bg-blue-400 hover:text-white transition-all flex items-center gap-2 border-2 border-white"
            >
              Block Explorer
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="http://localhost:3001"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-black border-2 border-white text-white font-bold hover:bg-blue-400 transition-all flex items-center gap-2"
            >
              AVO Station
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Roadmap;
